import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';

import { useMarketsSummaryState } from '#/features/perpetuals/market-info/markets-summary-context';
import InProgressIcon from '#/assets/InProgressIcon';
import { cEA135E } from '#/colors';
import { Resp, useApi } from '#/api/fetch-api';
import {
  appendOraclePrice,
  pauseOracleFeeds,
  resumeOracleFeeds,
} from '#/api/internal-utils';
import toast from '#/components/common/toast';

const INITIAL_MARKET = '';
const INITIAL_ORACLE_PRICE = '0';
const INITIAL_PAUSE_LIMIT = '600';

export default function OraclePrice() {
  const [activeMarket, setActiveMarket] = useState(INITIAL_MARKET);
  const [oraclePrice, setOraclePrice] = useState(INITIAL_ORACLE_PRICE);
  const [pauseLimit, setPauseLimit] = useState(INITIAL_PAUSE_LIMIT);
  const { marketsSummary, status, error } = useMarketsSummaryState();
  const [, execPauseOracleFeeds] = useApi(pauseOracleFeeds);
  const [, execResumeOracleFeeds] = useApi(resumeOracleFeeds);
  const [, execAppendOraclePrice] = useApi(appendOraclePrice);

  const handlePause = () => {
    execPauseOracleFeeds(
      { market: activeMarket, pause_time_seconds: pauseLimit },
      handleErrorResponse,
    );
  };

  const handleResume = () => {
    execResumeOracleFeeds({ market: activeMarket }, handleErrorResponse);
  };

  const handleSetOraclePrice = () => {
    execAppendOraclePrice(
      { market: activeMarket, price: oraclePrice },
      handleErrorResponse,
    );
  };

  /* Initialize form once with latest available API values */
  useEffect(() => {
    const isInitialized = oraclePrice !== INITIAL_ORACLE_PRICE;
    const defaultMarketSummary =
      marketsSummary.entities[marketsSummary.ids[0]!];

    if (isInitialized) return;
    if (defaultMarketSummary == null) return;

    setActiveMarket(defaultMarketSummary.symbol);
    if (defaultMarketSummary.oracle_price != null)
      setOraclePrice(defaultMarketSummary.oracle_price.toFixed(2));
  }, [marketsSummary, oraclePrice]);

  if (status === 'loading') return <InProgressIcon />;

  if (status === 'error') return <Error>{error}</Error>;

  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <Label>
        Market:
        <select
          name="markets"
          onChange={(e) => setActiveMarket(e.target.value)}
          value={activeMarket}
        >
          {marketsSummary.ids.map((market) => (
            <option key={market} value={market}>
              {market}
            </option>
          ))}
        </select>
      </Label>

      <Label>
        New Price:
        <Actions css="grid-template-columns: auto 1fr;">
          <input
            type="number"
            step="0.01"
            name="oraclePrice"
            value={oraclePrice}
            onChange={(e) => setOraclePrice(e.target.value)}
          />
          <button onClick={handleSetOraclePrice}>set</button>
        </Actions>
      </Label>

      <Label>
        Pause for:
        <Actions css="grid-template-columns: auto 1fr 1fr;">
          <input
            type="number"
            name="pauseLimit"
            value={pauseLimit}
            onChange={(e) => setPauseLimit(e.target.value)}
          />
          <button onClick={handlePause}>⏸</button>
          <button onClick={handleResume}>▶️</button>
        </Actions>
      </Label>

      <ApiData>{JSON.stringify(marketsSummary.entities, null, 2)}</ApiData>
    </Form>
  );
}

function handleErrorResponse<S = null>(resp: Resp<S>) {
  if (!resp.ok) {
    toast.error(resp.message);
  }
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 360px;

  & > * {
    margin-bottom: 12px;
  }
`;

const ApiData = styled.pre`
  font-size: 13px;
`;

const Label = styled.label`
  display: grid;
  grid-template-columns: 100px auto;
  align-items: center;
`;

const Actions = styled.div`
  display: grid;
  gap: 5px;
`;

const Error = styled.div`
  color: ${rgba(cEA135E, 0.7)};
`;
